import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Chip from '@mui/material/Chip';
import { Add, Visibility } from '@mui/icons-material';
import { Percent } from '@mui/icons-material';

const ContractDatasourceSelector = ({
  selectedSourceOptions,
  sourceOptions,
  setFormState,
  formState,
  sourceNotFound,
}) => {
  const reducerIdentifiers = formState.quantity?.reducerIdentifiers || [];
  const restIdentifiers = formState.quantity?.restIdentifiers || [];

  const cycleIdentifierType = (id) => {
    let newReducers = [...reducerIdentifiers];
    let newRests = [...restIdentifiers];

    if (!reducerIdentifiers.includes(id) && !restIdentifiers.includes(id)) {
      newReducers.push(id);
    } else if (reducerIdentifiers.includes(id)) {
      newReducers = newReducers.filter((rid) => rid !== id);
      newRests.push(id);
    } else {
      newRests = newRests.filter((rid) => rid !== id);
    }

    setFormState({
      ...formState,
      quantity: {
        ...(formState.quantity || {}),
        reducerIdentifiers: newReducers,
        restIdentifiers: newRests,
      },
    });
  };

  return (
    <Autocomplete
      multiple
      fullWidth
      value={selectedSourceOptions}
      options={sourceOptions}
      getOptionLabel={(option) =>
        `${option.companyName ? `[${option.companyName}] ` : ''}${option.name} - ${option.articleNumber}`
      }
      isOptionEqualToValue={(option, value) =>
        option.articleNumber === value.articleNumber
      }
      onChange={(event, newValues) => {
        const newIdentifiers = newValues.map((v) => v.articleNumber);
        setFormState({
          ...formState,
          quantity: {
            ...(formState.quantity || {}),
            identifiers: newIdentifiers,
            reducerIdentifiers: reducerIdentifiers.filter((id) =>
              newIdentifiers.includes(id),
            ),
            restIdentifiers: restIdentifiers.filter((id) =>
              newIdentifiers.includes(id),
            ),
          },
          type: 'DYNAMIC',
        });
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const isReducer = reducerIdentifiers.includes(option.articleNumber);
          const isRest = restIdentifiers.includes(option.articleNumber);

          let color = 'primary';
          let icon = <Add fontSize="small" />;
          let tooltip = 'Standard: Volle Quellmenge wird addiert.';

          if (isReducer) {
            color = 'warning';
            icon = <Visibility fontSize="small" />;
            tooltip =
              'Reducer: Markiert Menge als abgerechnet, addiert sie aber NICHT zur Position.';
          } else if (isRest) {
            color = 'primary';
            icon = <Percent fontSize="small" />;
            tooltip =
              'Rest: Nutzt nur die Menge, die nach vorherigen Reducern noch übrig ist.';
          }

          return (
            <Chip
              {...getTagProps({ index })}
              key={option.articleNumber}
              label={option.name}
              color={color}
              variant="outlined"
              icon={
                <Tooltip title={`Klicken zum Ändern: ${tooltip}`}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      cycleIdentifierType(option.articleNumber);
                    }}
                    sx={{ cursor: 'pointer', display: 'flex', pl: 1 }}
                  >
                    {icon}
                  </Box>
                </Tooltip>
              }
            />
          );
        })
      }
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
            <Box flexGrow={1}>
              <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>
                {option.companyName ? `[${option.companyName}] ` : ''}
                {option.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                {!option.isOverflow && `(${option.articleNumber}) `}
                {option.description}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 15,
                width: 120,
                textAlign: 'right',
                alignSelf: 'center',
              }}
            >
              {option.quantity} Stk.
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Quellen"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {sourceNotFound && (
                  <Tooltip title="Eine oder mehrere der ausgewählten Quellen konnten nicht gefunden werden.">
                    <WarningAmber color="warning" sx={{ mr: 1 }} />
                  </Tooltip>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ContractDatasourceSelector;
